/**
 * SectionLink: a link from one Settings card to another on the same panel
 * (no route change). It keeps a real `#<id>` href, scrolls with the console's
 * `scrollToSection` (sticky chrome cleared, reduced motion kept, the target
 * flash-highlighted) and moves focus to the target card's heading, so a
 * keyboard or screen reader user lands where the eye does.
 *
 * LinkedLine: a translated line with one `{{link}}` slot, rendered as a
 * SectionLink.
 */
import type { MouseEvent, ReactNode } from 'react';
import { scrollToSection } from './scrollToSection';

/** Focus the section's heading without a second scroll (the scroll is ours). */
function focusSectionHeading(section: HTMLElement): void {
  const heading = section.querySelector<HTMLElement>('h3') ?? section;
  if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
  heading.focus({ preventScroll: true });
}

export function SectionLink({ sectionId, children }: { sectionId: string; children: ReactNode }): JSX.Element {
  const onClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    const target = document.getElementById(sectionId);
    if (!target) return;
    event.preventDefault();
    scrollToSection(sectionId);
    focusSectionHeading(target);
  };
  return (
    <a
      href={`#${sectionId}`}
      onClick={onClick}
      className="font-medium text-primary-strong underline underline-offset-2 hover:text-primary-strong/80"
    >
      {children}
    </a>
  );
}

export function LinkedLine({
  text,
  linkText,
  sectionId,
}: {
  text: (link: string) => string;
  linkText: string;
  sectionId: string;
}): JSX.Element {
  const token = '\u0001';
  const [before, after = ''] = text(token).split(token);
  return (
    <>
      {before}
      <SectionLink sectionId={sectionId}>{linkText}</SectionLink>
      {after}
    </>
  );
}
